import { AccountOpIdentifiedBy } from '@ambire-common/libs/accountOp/submittedAccountOp'
import { getBenzinUrlParams } from '@ambire-common/utils/benzin'
import { BENZIN_URL } from '@env'

/**
 * Fallback used when `BENZIN_URL` is missing from the `.env` at build time
 * (`react-native-dotenv` runs with `allowUndefined: true`, so the import would
 * otherwise be `undefined` and we'd emit `undefined?chainId=…` links).
 */
const DEFAULT_BENZIN_URL = 'https://explorer.zkxwallet.com'

/**
 * Base URL of the ZKX-hosted Benzin explorer, normalized without a trailing
 * slash (`getBenzinUrlParams` returns a string starting with `?`, and whoever
 * fills in the `.env` may or may not type the trailing slash).
 *
 * One single domain for every environment — deliberately NOT following the
 * mobile-web DEV host swap (`toZkxApiHost` in `@common/config/env/env`, which
 * repoints apis.zkxwallet.com → apis.zkxteam.xyz). Consequence: that one
 * explorer is built against one relayer, so a `relayerId` minted by a different
 * relayer than the explorer's can't be resolved there. Links that already carry
 * a `txnId` are unaffected.
 */
export const BENZIN_EXPLORER_URL = (BENZIN_URL || DEFAULT_BENZIN_URL).replace(/\/+$/, '')

/**
 * Build a transaction explorer link.
 *
 * Mirrors Benzin's own "Open explorer" rule (see
 * `src/benzin/screens/BenzinScreen/hooks/useBenzin/useBenzin.ts`): the gate is
 * whether we already hold an on-chain `txnId`, NOT the `identifiedBy.type`.
 *
 * - Once a `txnId` is known we have a real block-explorer-resolvable tx hash —
 *   true for plain EOA txns AND for smart-account userOps after the
 *   bundler/relayer receipt resolves the EntryPoint tx hash — so we link to the
 *   network's own explorer (e.g. PulseScan, BscScan).
 * - No `txnId` yet (pending userOp / relayer that hasn't returned a hash) →
 *   fall back to Benzin, which decodes the userOp and resolves the final hash.
 *
 * Caveat: a userOp `txnId` is only final once confirmed; clicking during the
 * brief pre-confirmation window may momentarily 404. This is the same tradeoff
 * Benzin itself accepts.
 */
export const getTransactionExplorerLink = ({
  chainId,
  txnId,
  identifiedBy,
  explorerUrl
}: {
  chainId?: string | number | bigint
  txnId?: string | null
  identifiedBy?: AccountOpIdentifiedBy
  explorerUrl?: string
}): string => {
  if (explorerUrl && txnId) {
    return `${explorerUrl.replace(/\/$/, '')}/tx/${txnId}`
  }

  return `${BENZIN_EXPLORER_URL}${getBenzinUrlParams({ chainId, txnId, identifiedBy })}`
}
